import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, SxProps, Tooltip } from "@mui/material";
import { FormTextField, FormTextFieldProps } from "components/Forms/FormTextField";
import { ReactNode, useCallback, useState } from "react";
import { FieldValues, useController } from "react-hook-form";

type EditablePasswordFieldProps<T extends FieldValues> = {
  mode?: "add" | "edit";
  required?: boolean;
  onEditClick?: () => void;
  onCancelEdit?: () => void;
  canViewCredentials?: boolean;
  /** Auto-complete attribute for the password field. Defaults to "new-password"
   * to disable auto-fill and password manager saving of passwords.
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
   */
  autoComplete?: "new-password" | "off" | "on" | string;
} & FormTextFieldProps<T>;

function AdornmentButton({
  title,
  ariaLabel,
  onClick,
  disabled,
  sx,
  children,
}: {
  title: string;
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
  sx?: SxProps;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title} placement="top" arrow enterDelay={500} enterNextDelay={500}>
      <IconButton aria-label={ariaLabel} onClick={onClick} edge="end" disabled={disabled} sx={sx}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

export const EditablePasswordField = <T extends FieldValues>({
  mode,
  required,
  canViewCredentials = false,
  onCancelEdit,
  autoComplete = "new-password",
  ...props
}: EditablePasswordFieldProps<T>) => {
  const { onEditClick, control, name } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);

  // Use useController to get the field value
  const { field } = useController({
    name: name!,
    control,
  });

  const hasValue = Boolean(field.value);

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick();
    }
    setIsEditing(true);
  }, [onEditClick]);

  const toggleVisibility = useCallback(() => {
    setRevealPassword(!revealPassword);
  }, [revealPassword]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setRevealPassword(false);
    if (onCancelEdit) {
      onCancelEdit();
    }
  }, [onCancelEdit]);

  return (
    <FormTextField
      {...props}
      type={revealPassword ? "text" : "password"}
      disabled={(mode === "edit" && !isEditing) || props.disabled}
      textFieldProps={{
        required,
        onFocus: handleEditClick,
        autoComplete,
      }}
      InputLabelProps={{
        shrink: (mode === "edit" && !isEditing) || hasValue,
      }}
      InputProps={{
        placeholder: mode === "edit" && !isEditing ? "*********" : undefined,
        endAdornment: (
          <InputAdornment position="end">
            {/* View button - available when user can view credentials OR when editing */}
            {(canViewCredentials || (mode === "edit" && isEditing)) && (
              <AdornmentButton
                title={revealPassword ? "Hide password" : "Show password"}
                ariaLabel={revealPassword ? "hide password" : "show password"}
                onClick={toggleVisibility}
                disabled={props.disabled}
                sx={{ mr: mode === "edit" ? 1 : 0 }}
              >
                {revealPassword ? <VisibilityOff /> : <Visibility />}
              </AdornmentButton>
            )}

            {/* Edit/Cancel button - only in edit mode */}
            {mode === "edit" && !isEditing && (
              <AdornmentButton
                title="Edit password"
                ariaLabel="toggle password edit"
                onClick={handleEditClick}
                disabled={props.disabled}
              >
                <EditIcon />
              </AdornmentButton>
            )}
            {mode === "edit" && isEditing && (
              <AdornmentButton
                title="Cancel editing"
                ariaLabel="cancel password edit"
                onClick={handleCancelEdit}
                disabled={props.disabled}
              >
                <CloseIcon />
              </AdornmentButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};
